import { useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckSquare, FolderOpen, Heart, ListPlus, Music2, Play, RefreshCw, Shuffle, X } from "lucide-react";
import { GenreMix } from "@/components/music/GenreMix";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { FIELDS, genreKey, matches } from "@/lib/music/meta";
import { MoodPicker } from "@/components/music/MoodPicker";

const SHOW = 200;
export const sub = (t) => [t.artist, t.album, t.genre, t.year].filter(Boolean).join(" · ");

export const AddToPlaylist = ({ m, paths, testid, label }) => {
  const add = (p) => { m.pl.add(p.id, paths); if (paths.length > 1) toast.success(`Added ${paths.length.toLocaleString()} songs to “${p.name}”`); };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button title="Add to playlist" className={label ? "inline-flex items-center gap-1 rounded border border-[#2D3442] px-2 py-0.5 text-[11px] text-[#D6CCBC] hover:border-[#D97706]/50" : "rounded p-1 text-[#7A7468] hover:text-[#F3EBDD]"} data-testid={testid}><ListPlus className="h-3.5 w-3.5" />{label}</button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
        <DropdownMenuLabel className="text-xs">Add to playlist</DropdownMenuLabel>
        {m.playlists.map((p) => <DropdownMenuItem key={p.id} onClick={() => add(p)} data-testid={`${testid}-pl-${p.id}`}>{p.name}</DropdownMenuItem>)}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => { const p = m.pl.create(`Playlist ${m.playlists.length}`, paths); toast.success(`Added ${paths.length.toLocaleString()} ${paths.length === 1 ? "song" : "songs"} to “${p.name}”. Rename it in Playlists.`); }} data-testid={`${testid}-new`}>New playlist</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const TrackRow = ({ t, m, onPlay, extra, testid, selected, onSelect }) => (
  <li className={`group flex items-center gap-1.5 rounded px-1.5 py-1 text-xs transition-colors hover:bg-[#1A202C] ${m.current?.path === t.path ? "bg-[#D97706]/10" : ""}`} data-testid={testid}>
    {onSelect && <Checkbox checked={!!selected} onCheckedChange={onSelect} className="h-3.5 w-3.5" data-testid={`${testid}-select`} />}
    <button onClick={onPlay} className="flex min-w-0 flex-1 items-center gap-1.5 text-left" title={[t.title, sub(t), t.path].filter(Boolean).join("\n")}>
      <Play className="h-3 w-3 shrink-0 text-[#7A7468] group-hover:text-[#F59E0B]" />
      <span className="min-w-0 flex-1">
        <span className={`block truncate ${m.current?.path === t.path ? "text-[#FBBF24]" : "text-[#E8DFD1]"}`}>{t.title}</span>
        {sub(t) && <span className="block truncate text-[10px] text-[#7A7468]">{sub(t)}</span>}
      </span>
    </button>
    {m.favs.has(t.path) && <Heart className="h-3 w-3 shrink-0 fill-[#F87171] text-[#F87171]" data-testid={`${testid}-fav`} />}
    {extra}
  </li>
);

const Bulk = ({ m, sel, setSel, all }) => (
  <div className="flex flex-wrap items-center gap-1.5 rounded border border-[#D97706]/40 bg-[#D97706]/5 px-2 py-1.5 text-[11px]" data-testid="music-bulk-bar">
    <span className="text-[#F3EBDD]" data-testid="music-bulk-count">{sel.size.toLocaleString()} selected</span>
    {sel.size < all.length && <button onClick={() => setSel(new Set(all))} className="text-[#F59E0B] hover:underline" data-testid="music-select-all">Select all {all.length.toLocaleString()}</button>}
    <span className="flex-1" />
    {sel.size > 0 && <><AddToPlaylist m={m} paths={[...sel]} testid="music-bulk-add" label="Playlist" /><MoodPicker m={m} paths={[...sel]} testid="music-bulk-mood" label="Moods" /></>}
    <button onClick={() => setSel(null)} title="Done selecting" className="rounded p-0.5 text-[#7A7468] hover:text-[#F3EBDD]" data-testid="music-select-done"><X className="h-3.5 w-3.5" /></button>
  </div>
);

const Footer = ({ m }) => (
  <div className="flex flex-wrap items-center gap-3 text-[10px] text-[#7A7468]">
    {m.canRemember ? <button onClick={m.connect} className="inline-flex items-center gap-1 hover:text-[#F3EBDD]" data-testid="music-change-folder"><FolderOpen className="h-3 w-3" />Choose a different folder</button>
      : <label className="inline-flex cursor-pointer items-center gap-1 hover:text-[#F3EBDD]" data-testid="music-change-folder-label"><FolderOpen className="h-3 w-3" />Choose a different folder
          <input type="file" multiple webkitdirectory="" className="hidden" onChange={(e) => e.target.files?.length && m.pickFiles(e.target.files)} data-testid="music-change-folder-input" /></label>}
    <button onClick={m.forget} className="hover:text-[#F87171]" data-testid="music-forget-folder">{m.status === "session" ? "Clear this session's music" : `Disconnect “${m.dirName}”`}</button>
  </div>
);

const GenrePick = ({ m, genre, setGenre }) => (
  <Select value={genre} onValueChange={setGenre}>
    <SelectTrigger className="h-7 text-[11px]" data-testid="music-genre-select"><div className="flex min-w-0 items-center gap-1.5"><Music2 className="h-3 w-3 shrink-0 text-[#F59E0B]" /><SelectValue /></div></SelectTrigger>
    <SelectContent className="max-h-72">
      <SelectItem value="all" data-testid="music-genre-all">All genres</SelectItem>
      {m.genres.map((x) => <SelectItem key={x.key} value={x.key} data-testid={`music-genre-${x.key}`}>{x.name} ({x.count.toLocaleString()})</SelectItem>)}
    </SelectContent>
  </Select>
);

const MixBtn = ({ on, toggle }) => (
  <button onClick={toggle} title="Blend moods and genres" className={`inline-flex h-7 shrink-0 items-center gap-1 rounded-md border px-2 text-[11px] transition-colors ${on ? "border-[#D97706] text-[#FBBF24]" : "border-[#2D3442] text-[#A8A090] hover:text-[#F3EBDD]"}`} data-testid="music-mix-toggle"><Shuffle className="h-3 w-3" />Mix</button>
);

export const LibraryTab = ({ m }) => {
  const [q, setQ] = useState("");
  const [field, setField] = useState("all");
  const [sel, setSel] = useState(null);
  const [genre, setGenre] = useState("all");
  const [mixing, setMixing] = useState(false);
  const canMix = m.genres.length > 0 || Object.values(m.moods).some((v) => v.length);
  const g = m.genres.find((x) => x.key === genre);
  const list = useMemo(() => { const k = q.trim().toLowerCase(); return m.tracks.filter((t) => (!g || genreKey(t) === g.key) && matches(t, field, k)); }, [q, field, g, m.tracks]);
  const paths = useMemo(() => list.map((t) => t.path), [list]);
  const label = [g?.name, q && `“${q}”`].filter(Boolean).join(" · ") || "Library";
  const toggle = (p) => setSel((s) => { const n = new Set(s); if (n.has(p)) n.delete(p); else n.add(p); return n; });
  return (
    <div className="space-y-2" data-testid="music-library">
      <div className="flex items-center gap-1.5">
        <Select value={field} onValueChange={setField}>
          <SelectTrigger className="h-7 w-[104px] shrink-0 text-[11px]" data-testid="music-filter-field"><SelectValue /></SelectTrigger>
          <SelectContent>{FIELDS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`music-filter-field-${k}`}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${m.tracks.length.toLocaleString()} songs`} className="h-7 text-xs" data-testid="music-search" />
      </div>
      {m.genres.length > 0 && <div className="flex items-center gap-1.5">
        <div className="min-w-0 flex-1"><GenrePick m={m} genre={genre} setGenre={setGenre} /></div>
        <MixBtn on={mixing} toggle={() => setMixing(!mixing)} /></div>}
      {mixing && <GenreMix m={m} onClose={() => setMixing(false)} />}
      <div className="flex items-center gap-1 text-[11px] text-[#A8A090]">
        <span data-testid="music-result-count">{list.length.toLocaleString()} {list.length === 1 ? "song" : "songs"}</span>
        {m.metaProgress && <span className="text-[#7A7468]" data-testid="music-meta-progress">· reading details {m.metaProgress.done.toLocaleString()}/{m.metaProgress.total.toLocaleString()}</span>}
        <span className="flex-1" />
        {!m.genres.length && canMix && <MixBtn on={mixing} toggle={() => setMixing(!mixing)} />}
        <button onClick={() => m.play(paths, null, label)} title="Play all" className="rounded p-1 text-[#F59E0B] hover:bg-[#1A202C]" data-testid="music-play-all"><Play className="h-4 w-4" /></button>
        <button onClick={() => setSel(sel ? null : new Set())} title="Select songs" className={`rounded p-1 hover:bg-[#1A202C] ${sel ? "text-[#F59E0B]" : "text-[#7A7468]"}`} data-testid="music-select-mode"><CheckSquare className="h-3.5 w-3.5" /></button>
        {m.status === "ready" && <button onClick={m.rescan} title={`Check “${m.dirName}” for new songs`} className="rounded p-1 text-[#7A7468] hover:text-[#F3EBDD]" data-testid="music-rescan"><RefreshCw className="h-3.5 w-3.5" /></button>}
      </div>
      {sel && <Bulk m={m} sel={sel} setSel={setSel} all={paths} />}
      <ul className="space-y-0.5">
        {list.slice(0, SHOW).map((t, i) => (
          <TrackRow key={t.path} t={t} m={m} onPlay={() => m.play(paths, t.path, label)} testid={`music-track-${i}`} selected={sel?.has(t.path)} onSelect={sel ? () => toggle(t.path) : null}
            extra={!sel && <><MoodPicker m={m} paths={[t.path]} testid={`music-track-mood-${i}`} /><AddToPlaylist m={m} paths={[t.path]} testid={`music-track-add-${i}`} /></>} />
        ))}
      </ul>
      {list.length > SHOW && <p className="text-center text-[10px] text-[#7A7468]">Showing {SHOW} of {list.length.toLocaleString()}. Search to narrow down, or use Select all.</p>}
      {!list.length && <p className="py-3 text-center text-xs text-[#7A7468]">No songs match.</p>}
      <Footer m={m} />
    </div>
  );
};
